import { TIME_ACCELERATION } from './constants.js';
import { updateTelemetryUI } from './telemetry.js';
import { updateCometTrails } from './satellites.js';

let currentSimTime = null;
export function getCurrentSimTime() {
    // Return currentSimTime if available, otherwise return current time as fallback
    return currentSimTime || new Date();
}

export function setCurrentSimTime(newTime) {
    currentSimTime = new Date(newTime);
    // Update display immediately
    updateTimeDisplay(currentSimTime);
}

// Make it accessible globally for other modules
window.getCurrentSimTime = getCurrentSimTime;
window.setCurrentSimTime = setCurrentSimTime;

export function startSimulationLoop(scene, satelliteData, orbitalElements, simulationStartTime, getTimeMultiplier, advancedTexture, activeSatellite, telemetryData) {
    // Initialize simulation time from start time or use current time
    let simulationTime = simulationStartTime || new Date();
    
    // Initialize currentSimTime immediately
    currentSimTime = simulationTime;
    currentSimTime = simulationTime;
    // Update display according to persisted mode
    updateTimeDisplay(simulationTime);
    
    // Frame counter for performance optimizations
    let frameCounter = 0;
    let activeSatelliteList = [];
    
    // Pre-filter the satellite list for better performance
    for (const satName in orbitalElements) {
        if (satName !== 'metadata') {
            activeSatelliteList.push(satName);
        }
    }
    // --- Accurate time scaling ---
    let lastFrameTime = performance.now();
    // Use a less frequent update for better performance
    scene.registerBeforeRender(() => {
        // Always run update each frame for smoother orbital paths
        frameCounter++;

        const now = performance.now();
        const deltaRealSeconds = (now - lastFrameTime) / 1000;
        lastFrameTime = now;
        const timeMultiplier = getTimeMultiplier();
        // Expose current time multiplier globally for trail control
        window.currentTimeMultiplier = timeMultiplier;
        // Advance simulation time by real elapsed time * timeMultiplier * TIME_ACCELERATION
        const timeIncrement = timeMultiplier * TIME_ACCELERATION * deltaRealSeconds * 1000; // ms
        simulationTime = new Date(simulationTime.getTime() + timeIncrement);
        currentSimTime = simulationTime;

        // Only process visible satellites or limit to 5 at a time for smoother loading
        const visibleSats = frameCounter < 120 ? 
            activeSatelliteList.slice(0, Math.min(Math.floor(frameCounter/12), activeSatelliteList.length)) : 
            activeSatelliteList;

        // Position updates are handled in app.js render observer

        // Always update time display every simulation frame
        updateTimeDisplay(simulationTime);

        // Update telemetry UI if a satellite is active (keep this at lower frequency if needed)
        if (activeSatellite && frameCounter % 30 === 0) {
            updateTelemetryUI(activeSatellite, telemetryData);
        }
        
        // Update comet trails if in comet trail mode
        updateCometTrails();
    });
    
    return simulationTime;
}

export function updateTimeDisplay(simulationTime) {
    const el = document.getElementById('current-time');
    const simEl = document.getElementById('sim-current-time'); // New simulation time display
    
    // Safety check for null or undefined simulationTime
    if (!simulationTime || !(simulationTime instanceof Date)) {
        console.warn('Invalid simulationTime passed to updateTimeDisplay:', simulationTime);
        simulationTime = new Date(); // Fallback to current time
    }
    
    // Get time format from new setting
    const format = window.timeDisplayFormat || 'UTC';
    
    let timeString;
    let tzLabel = '';
    
    switch (format) {
        case 'LOCAL':
            // Local time
            timeString = simulationTime.toLocaleString();
            tzLabel = 'Local';
            break;
            
        case 'MET':
            // Mission Elapsed Time (from mission start)
            if (window.missionStartTime) {
                const elapsed = simulationTime.getTime() - window.missionStartTime.getTime();
                const days = Math.floor(elapsed / (1000 * 60 * 60 * 24));
                const hours = Math.floor((elapsed % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);
                
                if (days > 0) {
                    timeString = `T+${days}d ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                } else {
                    timeString = `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                }
                tzLabel = 'MET';
            } else {
                // Fallback to UTC if no mission start time
                const iso = simulationTime.toISOString().replace('T', ' ').substring(0, 19);
                timeString = iso;
                tzLabel = 'UTC';
            }
            break;
            
        case 'UTC':
        default:
            // UTC time (default)
            const iso = simulationTime.toISOString().replace('T', ' ').substring(0, 19);
            timeString = iso;
            tzLabel = 'UTC';
            break;
    }
    
    // Update both displays if they exist
    const displayText = tzLabel ? `${timeString} ${tzLabel}` : timeString;
    if (el) el.textContent = displayText;
    if (simEl) simEl.textContent = displayText;
    
    // Support old timezone setting for backwards compatibility
    const timezone = window.displayTimezone;
    if (timezone && timezone !== 'UTC' && format === 'UTC') {
        // Use old timezone system if set
        let oldTimeString;
        let oldTzLabel;
        
        switch (timezone) {
            case 'local':
                oldTimeString = simulationTime.toLocaleString();
                oldTzLabel = 'Local';
                break;
            case 'EST':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'America/New_York' });
                oldTzLabel = 'EST';
                break;
            case 'CST':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'America/Chicago' });
                oldTzLabel = 'CST';
                break;
            case 'MST':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'America/Denver' });
                oldTzLabel = 'MST';
                break;
            case 'PST':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
                oldTzLabel = 'PST';
                break;
            case 'CET':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'Europe/Berlin' });
                oldTzLabel = 'CET';
                break;
            case 'JST':
                oldTimeString = simulationTime.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' });
                oldTzLabel = 'JST';
                break;
        }
        
        if (oldTimeString) {
            const oldDisplayText = `${oldTimeString} ${oldTzLabel}`;
            if (el) el.textContent = oldDisplayText;
            if (simEl) simEl.textContent = oldDisplayText;
        }
    }
}
